import React, { useEffect, useMemo, useState } from "react";
import Layout from "../../components/Home/layout";
import { useTitle } from "../../hooks";
import { glossaryCourses, glossaryEntries } from "./glossaryData";
import styles from "./styles.module.scss";

const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const normalizeSearch = (value) => value.trim().toLocaleLowerCase();

const Glossary = () => {
  const [query, setQuery] = useState("");
  const [course, setCourse] = useState("all");
  const [activeLetter, setActiveLetter] = useState(glossaryEntries[0]?.letter || "A");

  useTitle("Train GRC | Cloud Audit and Cyber Risk Glossary");

  const filteredEntries = useMemo(() => {
    const search = normalizeSearch(query);
    return glossaryEntries.filter((entry) => {
      const matchesCourse = course === "all" || entry.course === course;
      const matchesSearch =
        !search ||
        entry.term.toLocaleLowerCase().includes(search) ||
        entry.definition.toLocaleLowerCase().includes(search) ||
        entry.course.toLocaleLowerCase().includes(search);
      return matchesCourse && matchesSearch;
    });
  }, [course, query]);

  const groupedEntries = useMemo(() => {
    const groups = new Map();
    filteredEntries.forEach((entry) => {
      if (!groups.has(entry.letter)) groups.set(entry.letter, []);
      groups.get(entry.letter).push(entry);
    });
    return [...groups.entries()];
  }, [filteredEntries]);

  const availableLetters = useMemo(
    () => new Set(groupedEntries.map(([letter]) => letter)),
    [groupedEntries]
  );

  useEffect(() => {
    if (!availableLetters.has(activeLetter)) {
      setActiveLetter(groupedEntries[0]?.[0] || "");
    }
  }, [activeLetter, availableLetters, groupedEntries]);

  useEffect(() => {
    if (!("IntersectionObserver" in window) || !groupedEntries.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible?.target?.dataset?.letter) {
          setActiveLetter(visible.target.dataset.letter);
        }
      },
      { rootMargin: "-18% 0px -72% 0px", threshold: 0 }
    );

    groupedEntries.forEach(([letter]) => {
      const section = document.getElementById(`letter-${letter.toLocaleLowerCase()}`);
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, [groupedEntries]);

  const clearFilters = () => {
    setQuery("");
    setCourse("all");
  };

  return (
    <Layout active="glossary">
      <div className={styles.glossaryPage}>
        <header className={styles.hero}>
          <div>
            <p className={styles.eyebrow}>Train GRC Academy reference</p>
            <h1>Cloud audit and cyber risk glossary</h1>
            <p>
              Find plain-language definitions for the platforms, controls, and
              technical concepts used throughout Train GRC Academy courses.
            </p>
          </div>
          <div className={styles.heroMark} aria-hidden="true">
            A–Z
          </div>
        </header>

        <section className={styles.controls} aria-labelledby="glossary-tools-heading">
          <div className={styles.controlHeading}>
            <p className={styles.eyebrow}>Search the reference</p>
            <h2 id="glossary-tools-heading">Find a term</h2>
          </div>
          <div className={styles.controlFields}>
            <label>
              <span>Search terms and definitions</span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Try “CloudTrail” or “container”"
              />
            </label>
            <label>
              <span>Course</span>
              <select value={course} onChange={(event) => setCourse(event.target.value)}>
                <option value="all">All courses</option>
                {glossaryCourses.map((courseName) => (
                  <option key={courseName} value={courseName}>
                    {courseName}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </section>

        <div className={styles.catalogShell}>
          <nav
            className={styles.letterIndex}
            aria-label="Glossary alphabetical index"
            data-glossary-index
          >
            <div className={styles.indexHeading}>
              <span>Table of contents</span>
              <strong>Browse by letter</strong>
            </div>
            <ol>
              {alphabet.map((letter) => {
                const isAvailable = availableLetters.has(letter);
                const isCurrent = activeLetter === letter;
                return (
                  <li key={letter}>
                    {isAvailable ? (
                      <a
                        href={`#letter-${letter.toLocaleLowerCase()}`}
                        aria-current={isCurrent ? "location" : undefined}
                        onClick={() => setActiveLetter(letter)}
                      >
                        <span aria-hidden="true">{letter}</span>
                        <span className={styles.letterName}>{letter}</span>
                      </a>
                    ) : (
                      <span className={styles.unavailableLetter} aria-label={`${letter}, no terms`}>
                        <span aria-hidden="true">{letter}</span>
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>

          <div className={styles.results} data-glossary-results>
            <div
              className={styles.resultsSummary}
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              <strong>{filteredEntries.length}</strong>{" "}
              {filteredEntries.length === 1 ? "term" : "terms"}
              {course !== "all" ? ` in ${course}` : " across Academy courses"}
            </div>

            {groupedEntries.length ? (
              groupedEntries.map(([letter, entries]) => (
                <section
                  className={styles.letterSection}
                  id={`letter-${letter.toLocaleLowerCase()}`}
                  data-letter={letter}
                  aria-labelledby={`letter-${letter.toLocaleLowerCase()}-heading`}
                  key={letter}
                >
                  <div className={styles.letterHeading}>
                    <h2 id={`letter-${letter.toLocaleLowerCase()}-heading`}>{letter}</h2>
                    <span>
                      {entries.length} {entries.length === 1 ? "term" : "terms"}
                    </span>
                  </div>
                  <dl className={styles.termList}>
                    {entries.map((entry) => (
                      <div className={styles.termCard} id={entry.id} key={entry.id}>
                        <dt>{entry.term}</dt>
                        <dd>
                          <span className={styles.courseTag}>{entry.course}</span>
                          <p>{entry.definition}</p>
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ))
            ) : (
              <div className={styles.emptyState} role="status">
                <p className={styles.eyebrow}>No matching terms</p>
                <h2>Try a broader search.</h2>
                <p>Clear the filters or search for another platform, control, or risk concept.</p>
                <button type="button" onClick={clearFilters}>
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Glossary;
